import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { useFlag, useHubspotChat } from '@sketch/modules-common'

// Utils
import { isTouchDevice } from '@sketch/utils'

// Constants
import { SKETCH_WEBSITE } from '@sketch/env-config'

// Components
import {
  useResponsiveDropdown,
  BaseResponsiveDropdownProps,
  SelectDropdownItem,
  SelectDropdownButtonItem,
  SelectDropdownExternalLinkItem,
} from '@sketch/components'
import { TroubleshootingConsentMenuItem } from '../TroubleshootingConsentMenuItem'

// Local Icons
import { icons } from '../../icons'

// Styles
import { Chevron } from '../BottomMenu/BottomMenu.styles'
import {
  DropdownDivider,
  DropdownWrapper,
  DropdownItem,
  DropdownHeader,
} from './HelpMenu.styles'

interface HelpMenuProps {
  title?: string
  userCanAdminister?: boolean
  workspaceId?: string
  workspaceName?: string
}

interface HelpMenuContentProps extends BaseResponsiveDropdownProps {
  hasHubspotChatSupportOn: boolean
  loadChat: () => void
  userCanAdminister?: boolean
  workspaceId?: string
  workspaceName?: string
}

/**
 *
 * HelpMenuContent
 *
 * Renders the content for the new Sidebar Help menu
 * found at the bottom of the sidebar
 */
const HelpMenuContent: React.FC<HelpMenuContentProps> = ({
  hasHubspotChatSupportOn,
  loadChat,
  currentVariant,
  userCanAdminister,
  workspaceId,
  workspaceName,
}) => {
  const isModalVariant = currentVariant === 'modal'
  const isTouch = isTouchDevice()

  return (
    <DropdownWrapper isModalVariant={isModalVariant}>
      {isModalVariant && <DropdownHeader>Help</DropdownHeader>}
      <DropdownItem>
        <SelectDropdownExternalLinkItem
          icon={icons.Home}
          href={`${SKETCH_WEBSITE}/home`}
          text="Visit Sketch.com"
        />
      </DropdownItem>
      <DropdownItem>
        <SelectDropdownExternalLinkItem
          icon={icons.Desktop}
          href={`${SKETCH_WEBSITE}/downloads/mac/`}
          text="Get Sketch for Mac"
        />
      </DropdownItem>
      <DropdownItem>
        <SelectDropdownExternalLinkItem
          icon={icons.Phone}
          href="https://apps.apple.com/app/sketch-view-and-mirror/id1609224699"
          text="Get Sketch for iOS"
        />
      </DropdownItem>
      <DropdownDivider />

      <DropdownItem>
        <SelectDropdownExternalLinkItem
          icon={icons.Notebook}
          href={`${SKETCH_WEBSITE}/docs/`}
          text="Documentation"
        />
      </DropdownItem>
      <DropdownItem>
        <SelectDropdownExternalLinkItem
          icon={icons.QuestionMark}
          href={`${SKETCH_WEBSITE}/support/`}
          text="Support"
        />
      </DropdownItem>
      {!isTouch && (
        <DropdownItem>
          <SelectDropdownExternalLinkItem
            icon={icons.Command}
            href={`${SKETCH_WEBSITE}/docs/designing/shortcuts/#web-app-shortcuts`}
            text="Keyboard Shortcuts"
          />
        </DropdownItem>
      )}
      {hasHubspotChatSupportOn && (
        <SelectDropdownButtonItem
          icon={icons.ChatIcon}
          onClick={loadChat}
          text="Chat with Us"
        />
      )}
      <DropdownItem>
        <SelectDropdownExternalLinkItem
          icon={icons.MailEnvelope}
          href={`${SKETCH_WEBSITE}/support/contact/`}
          text="Contact Us"
        />
      </DropdownItem>
      <DropdownItem>
        <SelectDropdownExternalLinkItem
          icon={icons.ChatBubbles}
          href="https://forum.sketch.com"
          text="Community Forum"
        />
      </DropdownItem>
      {userCanAdminister && (
        <>
          <DropdownDivider />
          <TroubleshootingConsentMenuItem
            workspaceId={workspaceId}
            workspaceName={workspaceName}
          />
        </>
      )}
    </DropdownWrapper>
  )
}

/**
 *
 * HelpMenu
 *
 * Renders the HelpMenu used in the "BottomMenu" on the "Sidebar"
 */
const HelpMenu: React.FC<HelpMenuProps> = ({
  title = 'Help with Sketch',
  userCanAdminister,
  workspaceId,
  workspaceName,
}) => {
  const hasHubspotChatSupportOn = useFlag('hubspot-chat-support')
  const { loadChat } = useHubspotChat()

  const { search } = useLocation()

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: HelpMenuContent,
    placement: 'right-end',
    dropdownProps: {
      hasHubspotChatSupportOn,
      loadChat,
      userCanAdminister,
      workspaceId,
      workspaceName,
    },
    showOnHover: true,
  })

  // only try to load the chat on the first load (avoids eternal loops)
  useEffect(() => {
    // check if chatWithUs is on the URl to auto load hubspot chat
    const URLparams = new URLSearchParams(search)
    const isChatWithUsLaunch = URLparams.get('chatWithUs')

    if (isChatWithUsLaunch === 'true') {
      loadChat()
    }
    // eslint-disable-next-line
  }, [])

  return (
    <>
      <SelectDropdownItem
        icon={icons.LifeBelt}
        text={title}
        additional={<Chevron />}
        {...buttonProps}
      />
      {content}
    </>
  )
}

export default HelpMenu
